import React, { useId } from 'react'
import { ArrowDown, ArrowUp, Ban, CalendarRange, RotateCcw } from 'lucide-react'
import CompetitorBadge from '@/components/market/CompetitorBadge'
import { formatMoney } from '@/lib/format'
import { MARKET_COLORS, rulerFraction } from '@/lib/market'
import { cn } from '@/lib/utils'
import type { MarketCompetitor, MarketNight, MarketNightCell, MarketPriceDomain } from '@/types/market'

/**
 * THE NIGHT'S LINEUP. One night drawn as a price ruler with every hotel on its own row: you first,
 * then the compset in its own order (the badge numbers). Rows never reorder, so as the selected
 * night changes the dots glide along the ruler and the market's shape changing night to night is
 * something you watch rather than read.
 *
 * Cells are matched to competitors by `marketPropertyId`, never by position. Positions on the
 * ruler come from `rulerFraction` over the server's `priceDomain`; they are never printed. Every
 * fact a dot shows is also in the row's text.
 */

/** With no change to report on the night, the change column folds away and the ruler gets the room. */
const ROW_GRID = {
  withChange:
    'grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 sm:grid-cols-[minmax(150px,200px)_minmax(0,1fr)_76px_minmax(0,210px)]',
  plain: 'grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 sm:grid-cols-[minmax(150px,200px)_minmax(0,1fr)_76px]',
}

function ChangeIcon({ cell }: { cell: MarketNightCell }) {
  if (cell.availabilityChange === 'sold_out') return <Ban className="h-3 w-3 shrink-0 text-slate-500" />
  if (cell.availabilityChange === 'back_on_sale') return <RotateCcw className="h-3 w-3 shrink-0 text-slate-500" />
  if (cell.direction === 'up') return <ArrowUp className="h-3 w-3 shrink-0 text-emerald-600" />
  if (cell.direction === 'down') return <ArrowDown className="h-3 w-3 shrink-0 text-rose-600" />
  return null
}

/** The ruler's faint gridlines, the compset band and the median line, repeated per row so they join. */
function TrackBackdrop({ night, domain }: { night: MarketNight; domain: MarketPriceDomain }) {
  const lo = night.compsetMin == null ? null : rulerFraction(night.compsetMin, domain)
  const hi = night.compsetMax == null ? null : rulerFraction(night.compsetMax, domain)
  return (
    <>
      {domain.ticks.map((tick) => (
        <span
          key={tick}
          className="absolute inset-y-0 w-px bg-slate-100"
          style={{ left: `${rulerFraction(tick, domain)}%` }}
        />
      ))}
      {lo != null && hi != null && (
        <span
          data-lineup-band
          className="absolute inset-y-0 bg-slate-200/70 transition-[left,width] duration-300 ease-out motion-reduce:transition-none"
          style={{ left: `${lo}%`, width: `max(${hi - lo}%, 2px)` }}
        />
      )}
      {night.compsetMedian != null && night.marketClaim && (
        <span
          className="absolute inset-y-0 w-0 border-l border-dashed border-slate-500/70 transition-[left] duration-300 ease-out motion-reduce:transition-none"
          style={{ left: `${rulerFraction(night.compsetMedian, domain)}%` }}
        />
      )}
    </>
  )
}

/** A hollow dot at last check's price: when the price moved, or when the hotel has since sold out. */
export function showsGhost(cell: MarketNightCell): boolean {
  return cell.previousPrice != null && (cell.soldOut || cell.direction === 'up' || cell.direction === 'down')
}

function Dot({
  value,
  domain,
  glide,
  className,
  children,
  testId,
}: {
  value: number
  domain: MarketPriceDomain
  glide: boolean
  className: string
  children?: React.ReactNode
  testId?: string
}) {
  return (
    <span
      data-testid={testId}
      className={cn(
        'absolute top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full',
        className
      )}
      style={{ left: `${rulerFraction(value, domain)}%`, transition: glide ? 'left 300ms ease-out' : 'none' }}
    >
      {children}
    </span>
  )
}

export default function NightLineup({
  night,
  competitors,
  numbers,
  youName,
  currency,
  domain,
  reducedMotion = false,
  onKeyDown,
}: {
  night: MarketNight
  competitors: MarketCompetitor[]
  numbers: Map<number, number>
  youName: string
  currency: string
  domain: MarketPriceDomain
  reducedMotion?: boolean
  /** Arrow keys on the lineup move the selected night. */
  onKeyDown?: (event: React.KeyboardEvent) => void
}) {
  const hintId = useId()
  const glide = !reducedMotion
  const cellsById = new Map(night.cells.map((c) => [c.marketPropertyId, c]))
  const money = (v: number) => formatMoney(v, currency)
  const hasGhosts = night.cells.some(showsGhost)
  const hasChange =
    night.cells.some((c) => c.changeLabel != null) || night.yourBookingComPrice != null || night.yourBookingComSoldOut
  const rowGrid = hasChange ? ROW_GRID.withChange : ROW_GRID.plain

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-1">
        <h4 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          The night's lineup
        </h4>
        <ul className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground" aria-label="Key">
          <li className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-primary ring-2 ring-primary/20" />
            You
          </li>
          {night.yourBookingComPrice != null && (
            <li className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full border-[1.5px] border-primary/70 bg-white" />
              Your Booking.com price
            </li>
          )}
          {hasGhosts && (
            <li className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full border border-dashed border-slate-400 bg-white" />
              Last check
            </li>
          )}
          {night.marketClaim && (
            <li className="flex items-center gap-1.5">
              <span className="h-3 w-0 border-l border-dashed border-slate-500" />
              Compset median
            </li>
          )}
        </ul>
      </div>

      <span id={hintId} className="sr-only">
        Use the left and right arrow keys to move between nights.
      </span>

      {/* The ruler: the server's ticks, labelled once above the rows. */}
      <div className={cn(rowGrid, 'px-3')} aria-hidden="true">
        <span className="hidden sm:block" />
        <div className="relative h-4">
          {domain.ticks.map((tick, i) => (
            <span
              key={tick}
              className={cn(
                'absolute top-0 text-[10px] tabular-nums text-muted-foreground',
                i === 0 ? 'translate-x-0' : i === domain.ticks.length - 1 ? '-translate-x-full' : '-translate-x-1/2'
              )}
              style={{ left: `${rulerFraction(tick, domain)}%` }}
            >
              {money(tick)}
            </span>
          ))}
        </div>
      </div>

      <ul
        aria-label={`Lineup for ${night.label}`}
        aria-describedby={onKeyDown ? hintId : undefined}
        tabIndex={onKeyDown ? 0 : undefined}
        onKeyDown={onKeyDown}
        className="overflow-hidden rounded-lg border border-border/60 outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      >
        {/* You */}
        <li
          className={cn(
            rowGrid,
            'items-center border-l-[3px] border-l-primary bg-primary/[0.04] py-2 pl-[9px] pr-3 sm:py-0'
          )}
        >
          <div className="col-span-2 flex min-w-0 items-center gap-2 sm:col-span-1">
            <CompetitorBadge isYou />
            <span className="truncate text-[13px] font-medium text-foreground" title={youName}>
              {youName}
            </span>
          </div>
          <div className="relative h-8 self-stretch sm:h-11" aria-hidden="true">
            <TrackBackdrop night={night} domain={domain} />
            {night.yourBookingComPrice != null && (
              <Dot
                value={night.yourBookingComPrice}
                domain={domain}
                glide={glide}
                className="h-3 w-3 border-[1.5px] border-primary/70 bg-white"
                testId="lineup-bookingcom"
              />
            )}
            {night.yourPrice != null && (
              <Dot
                value={night.yourPrice}
                domain={domain}
                glide={glide}
                className="h-3.5 w-3.5 bg-primary shadow-sm ring-[3px] ring-primary/20"
                testId="lineup-you"
              />
            )}
          </div>
          <div className="text-right text-sm font-semibold tabular-nums text-primary">
            {night.yourPrice == null ? (
              <span className="text-xs font-normal text-muted-foreground">Not priced</span>
            ) : (
              money(night.yourPrice)
            )}
          </div>
          {hasChange && (
            <div
              className="col-span-2 min-w-0 text-[11px] text-muted-foreground sm:col-span-1"
              title={night.yourBookingComSentence ?? undefined}
            >
              {night.yourBookingComPrice != null
                ? `${money(night.yourBookingComPrice)} on Booking.com`
                : night.yourBookingComSoldOut
                  ? 'Sold out on Booking.com'
                  : null}
            </div>
          )}
        </li>

        {competitors.map((competitor) => {
          const cell = cellsById.get(competitor.marketPropertyId)
          const number = numbers.get(competitor.marketPropertyId) ?? null
          const checked = cell?.checked ?? false
          const showGhost = cell != null && showsGhost(cell)
          const moveColor =
            cell?.direction === 'up'
              ? MARKET_COLORS.up
              : cell?.direction === 'down'
                ? MARKET_COLORS.down
                : MARKET_COLORS.bandEdge
          const from = showGhost ? rulerFraction(cell!.previousPrice!, domain) : null
          const to = cell?.price != null ? rulerFraction(cell.price, domain) : null

          return (
            <li
              key={competitor.marketPropertyId}
              data-testid={`lineup-row-${competitor.marketPropertyId}`}
              className={cn(rowGrid, 'items-center border-t border-border/40 py-2 pl-3 pr-3 sm:py-0')}
            >
              <div className="col-span-2 flex min-w-0 items-center gap-2 sm:col-span-1">
                <CompetitorBadge number={number} />
                <span className="truncate text-[13px] text-foreground" title={competitor.name}>
                  {competitor.name}
                </span>
                {cell?.minStayLabel && (
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-white px-1.5 py-0.5 text-[10px] font-semibold text-slate-700 ring-1 ring-inset ring-slate-300">
                    <CalendarRange className="h-3 w-3 text-slate-500" />
                    {cell.minStayLabel}
                  </span>
                )}
                {!checked && (
                  <span className="shrink-0 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">
                    Not checked
                  </span>
                )}
              </div>

              <div className="relative h-8 self-stretch sm:h-11" aria-hidden="true">
                <TrackBackdrop night={night} domain={domain} />
                {from != null && to != null && (
                  <span
                    className="absolute top-1/2 h-0.5 -translate-y-1/2 rounded-full"
                    style={{
                      left: `${Math.min(from, to)}%`,
                      width: `${Math.abs(to - from)}%`,
                      background: moveColor,
                      opacity: 0.55,
                      transition: glide ? 'left 300ms ease-out, width 300ms ease-out' : 'none',
                    }}
                  />
                )}
                {showGhost && (
                  <Dot
                    value={cell!.previousPrice!}
                    domain={domain}
                    glide={glide}
                    className="h-3 w-3 border border-dashed border-slate-400 bg-white"
                    testId={`lineup-ghost-${competitor.marketPropertyId}`}
                  />
                )}
                {cell?.price != null && (
                  <Dot
                    value={cell.price}
                    domain={domain}
                    glide={glide}
                    className="h-[18px] w-[18px] bg-slate-600 text-[10px] font-semibold text-white shadow-sm ring-2 ring-white"
                    testId={`lineup-dot-${competitor.marketPropertyId}`}
                  >
                    {number}
                  </Dot>
                )}
                {cell?.soldOut && (
                  <span className="absolute left-0 top-1/2 inline-flex -translate-y-1/2 items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-700 ring-1 ring-inset ring-slate-200">
                    <Ban className="h-3 w-3" />
                    Sold out
                  </span>
                )}
              </div>

              <div className="text-right text-sm font-semibold tabular-nums text-foreground">
                {cell?.price != null ? (
                  money(cell.price)
                ) : cell?.soldOut ? (
                  <span className="text-xs font-medium text-slate-600">Sold out</span>
                ) : (
                  <span className="text-xs font-normal text-muted-foreground">--</span>
                )}
              </div>

              {hasChange && (
                <div className="col-span-2 flex min-w-0 items-start gap-1 text-[11px] text-muted-foreground sm:col-span-1">
                  {cell?.changeLabel && (
                    <>
                      <ChangeIcon cell={cell} />
                      <span className="min-w-0 break-words leading-snug">{cell.changeLabel}</span>
                    </>
                  )}
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
